/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import Spinner from '@strava/ui/Spinner';
import cx from '@strava/ui/clsx';
import I18n from 'utils/I18n';
import { trackV2 } from 'utils/analytics';
import { followAthlete, unfollowAthlete } from 'api/athlete';
import EntryHeader from '../shared/media/EntryHeader';
import Activity from '../Activity';
import Card from '../shared/media/Card';
import {
  useEffectOnce,
  optOutApiCall,
  feedbackApiCall,
  undoApiCall,
  optOutReason
} from './SuggestedActivity.helpers';
import styles from './SuggestedActivity.scss';

// namespaced translations macro
const t = (keyPath, params) =>
  I18n.t(`components.feed.discovery.${keyPath}`, params);

// vs = visibility status
const vs = {
  VISIBLE: 'VISIBLE',
  HIDDEN: 'HIDDEN',
  HIDDEN_WITH_FEEDBACK_GIVEN: 'HIDDEN_WITH_FEEDBACK_GIVEN'
};

// Quick and dirty state management & error handling for api calls. Basically a
// minimal implementation of react-query with state tracking only.
//
// status can be 'idle', 'loading', 'error', or 'success'
const useMutation = (apiFn) => {
  const [status, setStatus] = useState('idle');
  const mutate = (...params) => {
    setStatus('loading');
    const unchainedPromise = apiFn(...params);
    unchainedPromise
      .then(() => setStatus('success'))
      .catch(() => setStatus('error'));
    return unchainedPromise;
  };
  return { status, mutate };
};

const SuggestedActivity = (props) => {
  const { activity, viewingAthlete } = props;

  // all stateful items are declared here:
  const [hideState, setHideState] = useState(vs.VISIBLE);
  const [didFollow, setDidFollow] = useState(false);
  const [reasonProvided, setReasonProvided] = useState(null);
  const optOutAction = useMutation(optOutApiCall);
  const feedbackAction = useMutation(feedbackApiCall);
  const undoAction = useMutation(undoApiCall);

  const followAction = useMutation(() =>
    followAthlete({
      followerId: viewingAthlete.id,
      followingId: activity.athlete.athleteId
    })
  );

  const unfollowAction = useMutation(() =>
    unfollowAthlete({
      followerId: viewingAthlete.id,
      followingId: activity.athlete.athleteId
    })
  );

  const anythingIsLoading =
    optOutAction.status === 'loading' ||
    feedbackAction.status === 'loading' ||
    undoAction.status === 'loading';

  // hit trackV2 with some partial application with the params that
  // are common to all of our track events within this scope so that we don't
  // have to repeat ourselves as much!
  const track = ({ properties, ...trackArgs }) =>
    trackV2({
      category: 'feed',
      ...trackArgs,
      properties: {
        suggested_activity_id: activity.id,
        suggested_athlete_id: activity.athlete.athleteId,
        ...properties
      }
    });

  const onClickHideActivity = async () => {
    track({
      page: 'activity',
      action: 'click',
      element: 'hide'
    });
    await optOutAction.mutate(activity.id);
    setHideState(vs.HIDDEN);
  };

  const onFeedback = async (reason) => {
    track({
      page: 'hide_activity_survey',
      action: 'click',
      element: reason
    });
    await feedbackAction.mutate(activity.id, reason);
    setReasonProvided(reason);
    setHideState(vs.HIDDEN_WITH_FEEDBACK_GIVEN);
  };

  const unHideActivity = async () => {
    track({
      page: 'activity',
      action: 'click',
      element: 'undo_hide'
    });
    if (reasonProvided) {
      await undoAction.mutate(activity.id, reasonProvided);
    }
    await undoAction.mutate(activity.id);
    setReasonProvided(null);
    setHideState(vs.VISIBLE);
  };

  const onToggleFollow = async () => {
    if (
      unfollowAction.status === 'loading' ||
      followAction.status === 'loading'
    ) {
      // noop if action is pending
      return;
    }

    if (didFollow === false) {
      track({
        page: 'activity',
        action: 'click',
        element: 'follow'
      });
      await followAction.mutate();
      setDidFollow(true);
    } else {
      track({
        page: 'activity',
        action: 'click',
        element: 'undo_follow'
      });
      await unfollowAction.mutate();
      setDidFollow(false);
    }
  };

  return (
    <>
      {/* Context header is always shown regardless of activity visibility state */}
      {activity.activityContextHeader && (
        <EntryHeader className={styles.contextHeader}>
          {activity.activityContextHeader.subtitle}
        </EntryHeader>
      )}
      <LoadingOverlay isLoading={anythingIsLoading}>
        {(() => {
          switch (hideState) {
            case vs.HIDDEN:
              return (
                <ActivityHidden
                  onFeedback={onFeedback}
                  onUndo={unHideActivity}
                  track={track}
                />
              );
            case vs.HIDDEN_WITH_FEEDBACK_GIVEN:
              return <ActivityHiddenWithFeedback onUndo={unHideActivity} />;
            default:
              return (
                <Activity
                  extraEntityActions={[
                    [
                      t('opt_out.hide_this_activity'),
                      { onSelect: onClickHideActivity }
                    ],
                    [
                      didFollow
                        ? t('undo_follow')
                        : t('follow_athlete', {
                            name: activity.athlete.firstName
                          }),
                      { onSelect: onToggleFollow }
                    ]
                  ]}
                  extraTrackingProperties={{
                    suggested_activity_id: activity.id,
                    suggested_athlete_id: activity.athlete.athleteId
                  }}
                  {...props}
                  entity="Activity"
                />
              );
          }
        })()}
      </LoadingOverlay>
    </>
  );
};

const LoadingOverlay = ({ children, isLoading }) => (
  <div className={cx(styles.loadingOverlay, isLoading && styles.isLoading)}>
    {children}
    {isLoading && (
      <div className={styles.loadingSpinner}>
        <Spinner />
      </div>
    )}
  </div>
);

const ActivityHidden = ({ onFeedback, onUndo, track }) => {
  useEffectOnce(() => {
    track({
      page: 'hide_activity_survey',
      action: 'screen_enter',
      element: ''
    });

    return () => {
      track({
        page: 'hide_activity_survey',
        action: 'screen_exit',
        element: ''
      });
    };
  });
  return (
    <Card className={styles.activityHidden}>
      <h1>{t('opt_out.activity_hidden')}</h1>
      <div className={styles.subhead}>{t('opt_out.hide_reason_question')}</div>
      <div className={styles.hiddenActivityActions}>
        <button
          onClick={() => onFeedback(optOutReason.uninterestedInSportType)}
        >
          {t('opt_out.not_interested_sport_type')}
        </button>
        <button onClick={() => onFeedback(optOutReason.uninterestedInAthlete)}>
          {t('opt_out.not_interested_athlete')}
        </button>
        <button onClick={() => onFeedback(optOutReason.notUseful)}>
          {t('opt_out.not_useful_or_motivating')}
        </button>
        <button onClick={() => onFeedback(optOutReason.other)}>
          {t('opt_out.none_of_the_above')}
        </button>
        <button onClick={onUndo}>{t('opt_out.undo_opt_out')}</button>
      </div>
    </Card>
  );
};

const ActivityHiddenWithFeedback = ({ onUndo }) => (
  <Card className={cx(styles.activityHidden, styles.withFeedback)}>
    <h1>{t('opt_out.activity_hidden')}</h1>
    <button onClick={onUndo}>{t('opt_out.undo_opt_out')}</button>
  </Card>
);

export default SuggestedActivity;
